import React, { useState } from 'react'
import {
  ConnectionAvatar,
  ConnectionsGrid,
  AddMinorModal,
  ConnectionSummary
} from '../components/connections'

/**
 * ConnectionsDemo Page
 *
 * Demonstration page showing all connection model components with sample data
 * This page shows how the components work together without requiring API calls
 */
function ConnectionsDemo() {
  const [showAddModal, setShowAddModal] = useState(false)

  // Sample connection data matching API response structure
  const sampleConnections = {
    connected: [
      {
        id: '1',
        first_name: 'David',
        last_name: 'Thompson',
        photo: null,
        relationship: 'Your Son',
        connection_type: 'connected',
        is_minor: false,
        is_online: true,
        guardian_name: null,
        guardian_display: null
      },
      {
        id: '2',
        first_name: 'Lisa',
        last_name: 'Thompson',
        photo: null,
        relationship: 'Your Daughter',
        connection_type: 'connected',
        is_minor: false,
        is_online: false,
        guardian_name: null,
        guardian_display: null
      }
    ],
    minors: [
      {
        id: '3',
        first_name: 'Emma',
        last_name: 'Thompson',
        photo: null,
        relationship: 'Your Granddaughter',
        connection_type: 'minor',
        is_minor: true,
        is_online: false,
        guardian_name: 'David',
        guardian_display: 'via David'
      },
      {
        id: '4',
        first_name: 'Tyler',
        last_name: 'Thompson',
        photo: null,
        relationship: 'Your Grandson',
        connection_type: 'minor',
        is_minor: true,
        is_online: false,
        guardian_name: 'David',
        guardian_display: 'via David'
      },
      {
        id: '5',
        first_name: 'Jack',
        last_name: 'Anderson',
        photo: null,
        relationship: 'Your Grandson',
        connection_type: 'minor',
        is_minor: true,
        is_online: false,
        guardian_name: 'Lisa',
        guardian_display: 'via Lisa'
      }
    ],
    pending: [],
    tree_only: [
      {
        id: '6',
        first_name: 'Sarah',
        last_name: 'Wilson',
        photo: null,
        relationship: 'Your Niece',
        connection_type: 'tree_only',
        is_minor: false,
        is_online: false,
        guardian_name: null,
        guardian_display: null
      }
    ]
  }

  const sampleSummary = {
    total_can_view: 5,
    adults: 2,
    minors: 3,
    pending: 0,
    tree_only: 1
  }

  const handleAddMinor = async (minorData) => {
    // In real usage, this would call the API via useConnections hook
    alert(`Minor "${minorData.first_name} ${minorData.last_name}" would be added via API`)
  }

  const handleConnectionClick = (connection) => {
    alert(`Clicked: ${connection.first_name} ${connection.last_name}`)
  }

  return (
    <div className="min-h-screen bg-[#F5F5DC] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#556B2F] mb-2">
            Connection Model Demo
          </h1>
          <p className="text-lg text-gray-600">
            Simplified connection management for "One Generation to Next"
          </p>
        </div>

        {/* Component Showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Left Column: Summary */}
          <div className="lg:col-span-1">
            <ConnectionSummary
              summary={sampleSummary}
              onAddMinorClick={() => setShowAddModal(true)}
              onManageClick={() => alert('Navigate to Connections page')}
            />

            {/* Avatar Showcase */}
            <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                ConnectionAvatar Sizes
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <ConnectionAvatar
                    connection={sampleConnections.connected[0]}
                    size="sm"
                  />
                  <span className="text-sm">Small (sm)</span>
                </div>
                <div className="flex items-center gap-3">
                  <ConnectionAvatar
                    connection={sampleConnections.connected[0]}
                    size="md"
                  />
                  <span className="text-sm">Medium (md)</span>
                </div>
                <div className="flex items-center gap-3">
                  <ConnectionAvatar
                    connection={sampleConnections.connected[0]}
                    size="lg"
                  />
                  <span className="text-sm">Large (lg)</span>
                </div>
                <div className="flex items-center gap-3">
                  <ConnectionAvatar
                    connection={sampleConnections.connected[0]}
                    size="xl"
                  />
                  <span className="text-sm">Extra Large (xl)</span>
                </div>
                <div className="flex items-center gap-3">
                  <ConnectionAvatar
                    connection={sampleConnections.minors[0]}
                    size="lg"
                  />
                  <span className="text-sm">Minor with 👶 badge</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Grid */}
          <div className="lg:col-span-2">
            <ConnectionsGrid
              connections={sampleConnections}
              onConnectionClick={handleConnectionClick}
              showTreeOnly={true}
              loading={false}
            />
          </div>
        </div>

        {/* Implementation Info */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Implementation Details
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-[#556B2F] mb-2">
                Backend (Django)
              </h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>PersonAccessLevel with MINOR = 5</li>
                <li>FamilyMember with is_minor, guardian_account</li>
                <li>Migration 0023_add_minor_support applied</li>
                <li>ConnectionSerializer, ConnectionSummarySerializer</li>
                <li>3 API endpoints: GET, POST, DELETE</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#556B2F] mb-2">
                Frontend (React)
              </h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>constants/connections.js - Types & helpers</li>
                <li>hooks/useConnections.js - Custom hook</li>
                <li>ConnectionAvatar - Avatar with badges</li>
                <li>ConnectionsGrid - Grouped display</li>
                <li>AddMinorModal - Form for adding children</li>
                <li>ConnectionSummary - Statistics widget</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="text-lg font-semibold text-green-800 mb-2">
              ✅ Implementation Complete
            </h3>
            <p className="text-green-700">
              All components are functional and ready for integration into the main application.
              This demo page can be accessed at <code className="bg-green-100 px-2 py-1 rounded">/connections-demo</code> route.
            </p>
          </div>
        </div>

        {/* Usage Example */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Usage Example
          </h2>
          <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">
{`import { useConnections } from '../hooks/useConnections'
import {
  ConnectionsGrid,
  ConnectionSummary,
  AddMinorModal
} from '../components/connections'

function MyDashboard() {
  const familyId = 'your-family-uuid'
  const {
    connections,
    summary,
    loading,
    addMinor,
    fetchConnections
  } = useConnections(familyId)

  const [showAddModal, setShowAddModal] = useState(false)

  return (
    <div>
      <ConnectionSummary
        summary={summary}
        onAddMinorClick={() => setShowAddModal(true)}
      />

      <ConnectionsGrid
        connections={connections}
        loading={loading}
      />

      <AddMinorModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={addMinor}
      />
    </div>
  )
}`}
          </pre>
        </div>
      </div>

      {/* Add Minor Modal */}
      <AddMinorModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddMinor}
        loading={false}
      />
    </div>
  )
}

export default ConnectionsDemo
